import { randomUUID } from "node:crypto";
import type { MedplumClient } from "@medplum/core";
import type { DocumentReference, Extension as FhirExtension } from "@medplum/fhirtypes";
import type { CreateResourceOptions } from "@vibrant/models/fhir/FhirResource";
import type { ResourceRef, SystemValuePair } from "@vibrant/models/fhir/fhir-resource-types";
import { hashFileContent } from "@/lib/crypto/hash-file-content";
import { isMedplumPreconditionFailedError } from "@/lib/errors/is-medplum-precondition-failed-error";
import { DocumentCategory, type DocumentCategoryCode } from "@/lib/fhir/document-categories";
import { resolveBinaryIdFromUrl } from "@/lib/files/utils";
import { stableStringify } from "@/lib/utils/stable-stringify";
import type { Document } from "./Document";
import { type DocumentModelAdapter, withDocumentReferenceSortPeriod } from "./document-file-write";

/**
 * Identifier system for the web upload key. Separate from `DOCUMENT_DEDUP_KEY_SYSTEM`: the import
 * key hashes subject + bytes + order linkage, while this key also folds in the practice and the
 * manual context a clinician chose at upload. Neither key reads the other, so an import and a web
 * upload of the same bytes stay separate documents.
 */
export const DOCUMENT_UPLOAD_KEY_SYSTEM = "vibrant.document.upload-key";

/** Bump when the preimage changes. Old keys stay valid for the documents that carry them. */
const DOCUMENT_UPLOAD_KEY_VERSION = "v1";

export interface DocumentUploadIdentityInput {
  practiceId: string;
  memberId: string;
  /** Hex SHA-256 of the stored bytes, computed server-side. */
  contentSha256Hex: string;
  manualCategoryCode?: DocumentCategoryCode;
  /** The UTC instant `normalizeServiceDateToUtcIso` stores, so the key matches the document. */
  manualServiceDate?: string;
}

/** The upload key as a FHIR identifier. The value is always a string, so it fits `Identifier[]`. */
export type DocumentUploadIdentity = SystemValuePair & { system: string; value: string };

/**
 * Builds the identity of one web upload: practice, member, verified bytes, and the manual context
 * that changes how the document is read. Absent manual context serializes as `null`, so "no
 * category" and an explicit category never share a key. Filename, upload time, and parser-written
 * state stay out on purpose: a renamed identical file is the same document.
 */
export function buildDocumentUploadIdentity(
  input: DocumentUploadIdentityInput,
): DocumentUploadIdentity {
  const manualContext = {
    category: input.manualCategoryCode
      ? { system: DocumentCategory.System, code: input.manualCategoryCode }
      : null,
    serviceDate: input.manualServiceDate ?? null,
  };
  const contextDigest = hashFileContent(
    Buffer.from(stableStringify(manualContext, "code-unit")),
  ).slice(0, 16);

  return {
    system: DOCUMENT_UPLOAD_KEY_SYSTEM,
    value: [
      DOCUMENT_UPLOAD_KEY_VERSION,
      input.practiceId,
      input.memberId,
      input.contentSha256Hex,
      contextDigest,
    ].join(":"),
  };
}

export function buildDocumentUploadIdentityQuery(identity: DocumentUploadIdentity): string {
  return `identifier=${identity.system}|${identity.value}`;
}

/**
 * The reuse inputs travel together: the key is built from the digest, and a match this call did
 * not create is checked against the digest. Absent as a whole, the create is unconditional and the
 * attachment carries no hash.
 */
export interface DocumentUploadReuse {
  identity: DocumentUploadIdentity;
  content: { sha256Base64: string; byteLength: number };
}

export interface CreateDocumentFromBinaryRequest {
  subject: ResourceRef;
  binaryId: string;
  contentType: string;
  fileName: string;
  /** Upload instant, used for `date` and the attachment `creation`. */
  date: string;
  reuse?: DocumentUploadReuse;
  extensions?: FhirExtension[];
  context?: DocumentReference["context"];
  category?: DocumentReference["category"];
}

/**
 * Identifier system for the per-call attempt marker. Each call stamps a fresh value on its payload,
 * and only a returned document that carries this call's value was created by this call. A retry
 * that resends an already-attached Binary therefore reads as a reuse, not a create.
 */
export const DOCUMENT_UPLOAD_ATTEMPT_SYSTEM = "vibrant.document.upload-attempt";

export type CreateDocumentFromBinaryResult =
  | {
      status: "ok";
      document: Document;
      wasCreated: boolean;
      /** The Binary the returned document points at; the request's own on a create. */
      winnerBinaryId: string | undefined;
    }
  /**
   * Nothing was written. `multiple-matches`: the key matched more than one document.
   * `winner-mismatch`: the key matched one document that is not this member's copy of these bytes.
   */
  | { status: "unresolved-identity"; reason: "multiple-matches" | "winner-mismatch" };

interface CreateDocumentFromUploadedBinaryInput {
  documentModel: DocumentModelAdapter;
  medplum: MedplumClient;
  request: CreateDocumentFromBinaryRequest;
  options?: CreateResourceOptions;
}

/**
 * Creates a DocumentReference for a Binary the browser already uploaded.
 *
 * With an identity, the create is conditional on the upload key, so two racing uploads of the same
 * bytes end in one document: Medplum returns the existing document to the loser. `wasCreated` comes
 * from the returned attachment, not from the response code, because the SDK hides the 200/201
 * split. The losing Binary is left in place. The finalizer does not own it, so it cannot prove that
 * nothing else references it.
 */
export async function createDocumentFromUploadedBinary({
  documentModel,
  medplum,
  request,
  options,
}: CreateDocumentFromUploadedBinaryInput): Promise<CreateDocumentFromBinaryResult> {
  const attemptId = randomUUID();
  const payload = withDocumentReferenceSortPeriod({
    status: "current" as const,
    docStatus: "final" as const,
    description: request.fileName,
    date: request.date,
    subject: request.subject,
    content: [
      {
        attachment: {
          contentType: request.contentType,
          url: `Binary/${request.binaryId}`,
          title: request.fileName,
          creation: request.date,
          hash: request.reuse?.content.sha256Base64,
          size: request.reuse?.content.byteLength,
        },
      },
    ],
    identifier: request.reuse
      ? [request.reuse.identity, { system: DOCUMENT_UPLOAD_ATTEMPT_SYSTEM, value: attemptId }]
      : undefined,
    extension: request.extensions,
    context: request.context,
    category: request.category,
  });

  const { reuse } = request;
  if (!reuse) {
    const created = await documentModel.createResource(medplum, payload, options);
    return {
      status: "ok",
      document: documentModel.createDocument(created),
      wasCreated: true,
      winnerBinaryId: request.binaryId,
    };
  }

  let resource: DocumentReference;
  try {
    resource = await documentModel.createResourceIfNoneExist(medplum, {
      resource: payload,
      ifNoneExistQuery: buildDocumentUploadIdentityQuery(reuse.identity),
      options,
    });
  } catch (error) {
    // Medplum answers 412 when the key already matches two documents. Picking one would hide the
    // duplication, so report it and leave the data as it is.
    if (isMedplumPreconditionFailedError(error)) {
      return { status: "unresolved-identity", reason: "multiple-matches" };
    }
    throw error;
  }

  // Only this call's attempt value proves a create. The Binary id cannot: a retry that resends an
  // already-attached Binary gets the existing document back with that same Binary.
  const wasCreated = (resource.identifier ?? []).some(
    (identifier) =>
      identifier.system === DOCUMENT_UPLOAD_ATTEMPT_SYSTEM && identifier.value === attemptId,
  );
  if (!wasCreated && !isMatchingWinner({ resource, subject: request.subject, reuse })) {
    return { status: "unresolved-identity", reason: "winner-mismatch" };
  }

  return {
    status: "ok",
    document: documentModel.createDocument(resource),
    wasCreated,
    winnerBinaryId: resolveBinaryIdFromUrl(resource.content?.[0]?.attachment?.url ?? ""),
  };
}

/**
 * A match this call did not create must be the member's own document with the same bytes. The
 * key already encodes both, but a key is an identifier any writer can set, so the winner is
 * checked against the subject and the attachment hash before it is handed back as a reuse. The
 * hash is the writer's own claim: a document staged for the same member with other bytes and a
 * copied hash still passes, so this closes the wrong-member case, not a same-member forgery.
 */
function isMatchingWinner({
  resource,
  subject,
  reuse,
}: {
  resource: DocumentReference;
  subject: ResourceRef;
  reuse: DocumentUploadReuse;
}): boolean {
  const attachment = resource.content?.[0]?.attachment;
  return (
    resource.subject?.reference === subject.reference &&
    attachment?.hash === reuse.content.sha256Base64
  );
}
